import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  doc,
  deleteDoc,
  updateDoc,
  addDoc,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase';

const UnregisteredAccessPanel = ({ setCurrentPage }) => {
  const [loading, setLoading] = useState(true);
  const [accessLogs, setAccessLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [currentPage, setCurrentPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);
  const [warningMessage, setWarningMessage] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [sendingWarning, setSendingWarning] = useState(false);
  const [userNames, setUserNames] = useState({});
  const [userAccessCounts, setUserAccessCounts] = useState({});
  const [showChangeDeviceModal, setShowChangeDeviceModal] = useState(false);
  const [changingDevice, setChangingDevice] = useState(false);

  const logsPerPage = 10;

  useEffect(() => {
    fetchAccessLogs();
  }, [filterStatus, currentPage]);

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      
      // Get all documents
      const allSnapshot = await getDocs(collection(db, 'unregistered_access'));
      const allAccessLogs = allSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          // Convert timestamp to JS Date if it exists
          timestamp: data.timestamp ? new Date(data.timestamp.toDate()) : new Date(),
        };
      });

      // Group logs by userId and get latest access for each user
      const userLogsMap = {};
      const accessCounts = {};
      
      allAccessLogs.forEach(log => {
        if (log.userId) {
          // Count all accesses per user
          accessCounts[log.userId] = (accessCounts[log.userId] || 0) + 1;
          
          // Keep only the latest log for each user
          if (!userLogsMap[log.userId] || log.timestamp > userLogsMap[log.userId].timestamp) {
            userLogsMap[log.userId] = {
              ...log,
              accessCount: accessCounts[log.userId]
            };
          }
        }
      });
      
      // Update access counts for all user logs
      Object.keys(userLogsMap).forEach(userId => {
        userLogsMap[userId].accessCount = accessCounts[userId];
      });
      
      setUserAccessCounts(accessCounts);

      // Convert map to array and apply filters
      let groupedLogs = Object.values(userLogsMap);
      
      // Apply status filter
      if (filterStatus !== 'all') {
        groupedLogs = groupedLogs.filter(log => log.actionTaken === filterStatus);
      }
      
      // Sort by timestamp - most recent first
      groupedLogs.sort((a, b) => b.timestamp - a.timestamp);

      // Get unique user IDs to fetch their display names
      const userIds = groupedLogs.map(log => log.userId);
      const userNameMap = {};
      
      // Fetch user names from users collection
      if (userIds.length > 0) {
        const userPromises = userIds.map(async (userId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              userNameMap[userId] = userData.displayName || (userData.email ? userData.email.split('@')[0] : 'Unknown');
            }
          } catch (error) {
            console.error(`Error fetching user data for ${userId}:`, error);
          }
        });
        
        await Promise.all(userPromises);
        setUserNames(userNameMap);
      }
      
      // Calculate total pages
      setTotalPages(Math.ceil(groupedLogs.length / logsPerPage));
      
      // Apply pagination to the grouped data
      const startIndex = (currentPage - 1) * logsPerPage;
      const paginatedLogs = groupedLogs.slice(startIndex, startIndex + logsPerPage);
      
      setAccessLogs(paginatedLogs);
    } catch (error) {
      console.error("Error fetching access logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChangeRegisteredDevice = async () => {
    if (!selectedLog || !selectedLog.userId) return;
    
    try {
      setChangingDevice(true);
      
      // Get the current user document
      const userRef = doc(db, "users", selectedLog.userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        alert('User document not found.');
        return;
      }
      
      // Create device details object from access log
      const deviceDetails = {
        brand: selectedLog.accessDevice?.brand || '',
        model: selectedLog.accessDevice?.model || '',
        deviceName: selectedLog.accessDevice?.deviceName || '',
        systemName: selectedLog.accessDevice?.systemName || '',
        systemVersion: selectedLog.accessDevice?.systemVersion || '',
        ipAddress: selectedLog.accessDevice?.ipAddress || '',
        connectionType: selectedLog.accessDevice?.connectionType || 'UNKNOWN',
        isConnected: true,
        timestamp: new Date().toISOString(),
        deviceId: selectedLog.accessDevice?.deviceId || 'unknown_device'
      };
      
      // First update the log (this won't trigger any listeners that matter)
      await updateDoc(doc(db, "unregistered_access", selectedLog.id), {
        actionTaken: 'device_changed',
        reviewedAt: serverTimestamp(),
        updatedByAdmin: auth.currentUser?.email || 'unknown_admin'
      });
      
      // Now update the user document (this is what triggers the app's Firestore listener)
      await updateDoc(userRef, {
        'subscription.registeredDevice': selectedLog.accessDevice?.deviceId || 'unknown_device',
        'subscription.deviceDetails': deviceDetails,
        'subscription.unregisteredAccessStart': null, // Reset unregistered access tracking
        'subscription.lastDeviceChangeAt': serverTimestamp(), // Add timestamp for when device was changed
        'subscription.lastDeviceChangedBy': auth.currentUser?.email || 'unknown_admin'
      });
      
      // Create a user notification
      try {
        await updateDoc(userRef, {
          notifications: [{
            type: 'device_changed',
            message: 'Your registered device has been updated by an administrator.',
            timestamp: serverTimestamp(),
            read: false
          }, ...(userDoc.data().notifications || [])]
        });
      } catch (error) {
        console.error("Error creating notification:", error);
      }
      
      alert('Registered device changed successfully!');
      closeChangeDeviceModal();
      fetchAccessLogs(); // Refresh the logs
    } catch (error) {
      console.error("Error changing registered device:", error);
      alert('Failed to change registered device: ' + error.message);
    } finally {
      setChangingDevice(false);
    }
  };
  
// Add this function to open the change device modal
const openChangeDeviceModal = (log) => {
  setSelectedLog(log);
  setShowChangeDeviceModal(true);
};

// Add this function to close the change device modal
const closeChangeDeviceModal = () => {
  setSelectedLog(null);
  setShowChangeDeviceModal(false);
};

  // Handle deletion of all logs for a user
  const handleDeleteLog = async (log) => {
    const userName = userNames[log.userId] || (log.userEmail ? log.userEmail.split('@')[0] : 'User');
    const accessCount = log.accessCount || 1;
    
    if (!window.confirm(`Are you sure you want to delete ALL ${accessCount} access log${accessCount > 1 ? 's' : ''} for user "${userName}"? This action cannot be undone.`)) return;
    
    try {
      setLoading(true);
      
      // Get all logs for this user
      const allLogsQuery = query(
        collection(db, 'unregistered_access'),
        where('userId', '==', log.userId)
      );
      
      const snapshot = await getDocs(allLogsQuery);
      
      // Delete all logs for this user using batch
      const batch = writeBatch(db);
      snapshot.docs.forEach((logDoc) => {
        batch.delete(logDoc.ref);
      });
      
      await batch.commit();
      
      alert(`Successfully deleted all ${snapshot.docs.length} access log(s) for user "${userName}".`);
      
      // Refresh logs after deletion
      fetchAccessLogs();
    } catch (error) {
      console.error("Error deleting user access logs:", error);
      alert('Failed to delete logs.');
    } finally {
      setLoading(false);
    }
  };

  // Handle marking a log as reviewed
  const handleMarkAsReviewed = async (logId) => {
    try {
      setLoading(true);
      await updateDoc(doc(db, 'unregistered_access', logId), {
        actionTaken: 'reviewed',
        reviewedAt: serverTimestamp()
      });
      // Refresh logs after update
      fetchAccessLogs();
    } catch (error) {
      console.error("Error updating access log:", error);
      alert('Failed to update log.');
    } finally {
      setLoading(false);
    }
  };

  // Open warning message modal
  const openWarningModal = (log) => {
    setSelectedLog(log);
    const userName = userNames[log.userId] || (log.userEmail ? log.userEmail.split('@')[0] : 'User');
    const accessCount = userAccessCounts[log.userId] || 1;
    
    setWarningMessage(`Dear ${userName},\n\nOur system detected an unregistered device access to your account on ${formatDate(log.timestamp)}. This is the ${getOrdinal(accessCount)} time we've detected an unregistered device access for your account. Please note that your subscription allows access from only one registered device. If this wasn't you, please secure your account immediately.\n\nBest regards,\nSupport Team`);
    setShowWarningModal(true);
  };

  // Close warning message modal
  const closeWarningModal = () => {
    setSelectedLog(null);
    setWarningMessage('');
    setShowWarningModal(false);
  };

  // Helper function to get ordinal suffix
  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Send warning message to user
const sendWarningMessage = async () => {
  if (!selectedLog || !warningMessage.trim()) return;

  try {
    setSendingWarning(true);
    
    // Save the warning message to Firestore
    await addDoc(collection(db, 'user_warnings'), {
      userId: selectedLog.userId,
      userEmail: selectedLog.userEmail,
      message: warningMessage,
      relatedLogId: selectedLog.id,
      sentAt: serverTimestamp(),
      read: false,
      type: 'device_warning' // Add a type field to categorize warnings
    });
    
    // Update the log to indicate a warning was sent
    await updateDoc(doc(db, 'unregistered_access', selectedLog.id), {
      actionTaken: 'warning_sent',
      warningSentAt: serverTimestamp()
    });
    
    // Also notify the user through a notification
    try {
      // Check if user has a notifications array in their document
      const userRef = doc(db, 'users', selectedLog.userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        // Add a notification to the user's document
        await updateDoc(userRef, {
          notifications: [{
            type: 'device_warning',
            message: 'You have received a security warning about unauthorized device access.',
            timestamp: serverTimestamp(),
            read: false
          }, ...(userDoc.data().notifications || [])]
        });
        console.log("Added notification to user document");
      }
    } catch (error) {
      console.error("Error creating user notification:", error);
      // Non-critical error, continue anyway
    }
    
    alert('Warning message sent successfully!');
    closeWarningModal();
    fetchAccessLogs();
  } catch (error) {
    console.error("Error sending warning message:", error);
    alert('Failed to send warning message.');
  } finally {
    setSendingWarning(false);
  }
};

  // Filter logs based on search term
  const filteredLogs = accessLogs.filter(log => {
    const searchFields = [
      log.userEmail || '',
      userNames[log.userId] || '',
      log.userId || '',
      log.accessDevice?.brand || '',
      log.accessDevice?.model || '',
      log.accessDevice?.deviceName || ''
    ].map(field => field.toLowerCase());
    
    const lowercaseSearchTerm = searchTerm.toLowerCase();
    
    return searchFields.some(field => field.includes(lowercaseSearchTerm));
  });

  const formatDate = (date) => {
    if (!date) return 'Unknown';
    return new Date(date).toLocaleString();
  };

  // Pagination handlers
  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPageNumber(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPageNumber(currentPage + 1);
    }
  };

  // Get status badge class based on action taken
  const getStatusBadgeClass = (actionTaken) => {
    switch(actionTaken) {
      case 'reviewed':
        return 'bg-green-100 text-green-800';
      case 'warning_sent':
        return 'bg-orange-100 text-orange-800';
      case 'device_changed':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };  

  // Get status text based on action taken
const getStatusText = (actionTaken) => {
  switch(actionTaken) {
    case 'reviewed':
      return 'Reviewed';
    case 'warning_sent':
      return 'Warning Sent';
    case 'device_changed':
      return 'Device Changed';
    default:
      return 'Unreviewed';
  }
};

  // Get access count risk level class
  const getAccessCountClass = (count) => {
    if (count >= 5) return "text-red-600 font-bold";
    if (count >= 3) return "text-orange-600 font-semibold";
    return "text-gray-700";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-lg border-b border-slate-200/60 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl shadow-lg">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                  Security Monitor
                </h1>
                <p className="text-slate-600 mt-1">Unregistered Device Access Control</p>
              </div>
            </div>
            <button
              onClick={() => setCurrentPage('AdminHome')}
              className="group relative inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:-translate-x-1 transition-transform duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 pt-4 pb-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="group relative bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-slate-200/50 hover:border-yellow-300/50">
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-400/10 to-orange-400/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center">
              <div className="p-4 bg-gradient-to-br from-yellow-500 to-orange-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Unreviewed Users</p>
                <p className="text-3xl font-bold text-slate-900">{Object.values(userAccessCounts).filter((_, index) => {
                  const users = Object.keys(userAccessCounts);
                  const user = users[index];
                  const userLog = accessLogs.find(log => log.userId === user);
                  return userLog && userLog.actionTaken !== 'reviewed' && userLog.actionTaken !== 'warning_sent' && userLog.actionTaken !== 'device_changed';
                }).length}</p>
              </div>
            </div>
          </div>

          <div className="group relative bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-slate-200/50 hover:border-green-300/50">
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/10 to-emerald-400/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center">
              <div className="p-4 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Total Users</p>
                <p className="text-3xl font-bold text-slate-900">{Object.keys(userAccessCounts).length}</p>
              </div>
            </div>
          </div>

          <div className="group relative bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-slate-200/50 hover:border-blue-300/50">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center">
              <div className="p-4 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Total Attempts</p>
                <p className="text-3xl font-bold text-slate-900">{Object.values(userAccessCounts).reduce((sum, count) => sum + count, 0)}</p>
              </div>
            </div>
          </div>

          <div className="group relative bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-6 border border-slate-200/50 hover:border-red-300/50">
            <div className="absolute inset-0 bg-gradient-to-br from-red-400/10 to-pink-400/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative flex items-center">
              <div className="p-4 bg-gradient-to-br from-red-500 to-pink-500 rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-slate-600">Repeat Offenders</p>
                <p className="text-3xl font-bold text-slate-900">{Object.values(userAccessCounts).filter(count => count >= 3).length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-end gap-6">
            <div className="flex-1">
              <label htmlFor="search" className="block text-sm font-semibold text-slate-700 mb-2">Search Users</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  id="search"
                  placeholder="Search by email, name, device..."
                  className="pl-12 pr-4 py-3 w-full border border-slate-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="w-full lg:w-64">
              <label htmlFor="status" className="block text-sm font-semibold text-slate-700 mb-2">Filter by Status</label>
              <select
                id="status"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="logged">Unreviewed</option>
                <option value="reviewed">Reviewed</option>
                <option value="warning_sent">Warning Sent</option>
              </select>
            </div>
            
            <div className="w-full lg:w-auto">
              <button
                onClick={fetchAccessLogs}
                className="group w-full lg:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:rotate-180 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Access Logs Table */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl shadow-lg border border-slate-200/50 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-slate-200/50 bg-gradient-to-r from-slate-50 to-white">
            <h3 className="text-lg font-semibold text-slate-900">Security Access Monitor</h3>
            <p className="text-sm text-slate-600 mt-1">Review and manage unregistered device access attempts</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-gradient-to-r from-slate-50 to-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">User Information</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Device Details</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Access Count</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Subscription</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Latest Access</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Status</th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                            <span className="text-red-700 font-medium">
                              {(userNames[log.userId] || log.userEmail || '?').charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {userNames[log.userId] || (log.userEmail ? log.userEmail.split('@')[0] : 'Unknown')}
                            </div>
                            <div className="text-sm text-gray-500">{log.userEmail || 'No Email'}</div>
                            <div className="text-xs text-gray-400">ID: {log.userId}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {log.accessDevice?.deviceName || log.accessDevice?.model || 'Unknown Device'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {log.accessDevice?.brand} {log.accessDevice?.model} ({log.accessDevice?.systemName} {log.accessDevice?.systemVersion})
                        </div>
                        <div className="text-xs text-gray-500">
                          IP: {log.accessDevice?.ipAddress || 'Unknown'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={`text-sm font-medium ${getAccessCountClass(log.accessCount || 1)}`}>
                          {log.accessCount || 1} {log.accessCount > 1 ? 'Times' : 'Time'}
                        </div>
                        {log.accessCount >= 3 && (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                            Repeat Offender
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{log.subscriptionPlan || 'Unknown'}</div>
                        <div className="text-xs text-gray-500">
                          Registered: {log.registeredDeviceId || 'None'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(log.timestamp)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          getStatusBadgeClass(log.actionTaken)
                        }`}>
                          {getStatusText(log.actionTaken)}
                        </span>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
  <div className="flex space-x-2">
    {log.actionTaken !== 'reviewed' && log.actionTaken !== 'warning_sent' && (
      <>
        <button 
          onClick={() => handleMarkAsReviewed(log.id)}
          className="text-indigo-600 hover:text-indigo-900 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          Mark Reviewed
        </button>
        <button 
          onClick={() => openWarningModal(log)}
          className="text-orange-600 hover:text-orange-900 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          Send Warning
        </button>
        {/* Add the new Change Device button */}
        <button 
          onClick={() => openChangeDeviceModal(log)}
          className="text-blue-600 hover:text-blue-900 flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          Change Device
        </button>
      </>
    )}
    <button 
      onClick={() => handleDeleteLog(log)}
      className="text-red-600 hover:text-red-900 flex items-center"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
      Delete All ({log.accessCount || 1})
    </button>
  </div>
</td>

{/* Change Registered Device Modal */}
{showChangeDeviceModal && selectedLog && (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] flex flex-col">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">
          Change Registered Device for {userNames[selectedLog?.userId] || (selectedLog?.userEmail ? selectedLog.userEmail.split('@')[0] : 'User')}
        </h3>
        <button
          onClick={closeChangeDeviceModal}
          className="text-gray-400 hover:text-gray-500 focus:outline-none"
        >
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="p-4 flex-1 overflow-auto">
        <div className="space-y-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">
              You are about to change the registered device for this user. This will allow them to access the application from this new device.
            </p>
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="flex items-center mb-2">
                <div className="text-sm font-medium text-gray-700">User:</div>
                <div className="ml-2 text-sm text-gray-900">{userNames[selectedLog?.userId] || 'Unknown User'}</div>
              </div>
              <div className="flex items-center mb-2">
                <div className="text-sm font-medium text-gray-700">Email:</div>
                <div className="ml-2 text-sm text-gray-900">{selectedLog?.userEmail || 'No Email Available'}</div>
              </div>
              <div className="flex items-center mb-2">
                <div className="text-sm font-medium text-gray-700">User ID:</div>
                <div className="ml-2 text-sm text-gray-900">{selectedLog?.userId || 'Unknown'}</div>
              </div>
              <div className="flex items-center mb-2">
                <div className="text-sm font-medium text-gray-700">Current Registered Device:</div>
                <div className="ml-2 text-sm text-gray-900">{selectedLog?.registeredDeviceId || 'None'}</div>
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <h4 className="text-sm font-semibold text-blue-800 mb-2">New Device Information:</h4>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-sm text-gray-700">Device Name:</div>
                  <div className="text-sm font-medium">{selectedLog?.accessDevice?.deviceName || 'Unknown'}</div>
                  
                  <div className="text-sm text-gray-700">Brand:</div>
                  <div className="text-sm font-medium">{selectedLog?.accessDevice?.brand || 'Unknown'}</div>
                  
                  <div className="text-sm text-gray-700">Model:</div>
                  <div className="text-sm font-medium">{selectedLog?.accessDevice?.model || 'Unknown'}</div>
                  
                  <div className="text-sm text-gray-700">System:</div>
                  <div className="text-sm font-medium">{selectedLog?.accessDevice?.systemName} {selectedLog?.accessDevice?.systemVersion}</div>
                  
                  <div className="text-sm text-gray-700">IP Address:</div>
                  <div className="text-sm font-medium">{selectedLog?.accessDevice?.ipAddress || 'Unknown'}</div>
                  
                  <div className="text-sm text-gray-700">Device ID:</div>
                  <div className="text-sm font-medium">{selectedLog?.accessDevice?.deviceId || 'Unknown'}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 p-3 rounded-md">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  This action will replace the user's currently registered device with this new device. The user will receive a notification about this change.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 border-t border-gray-200 flex justify-end space-x-4">
        <button
          onClick={closeChangeDeviceModal}
          className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Cancel
        </button>
        <button
          onClick={handleChangeRegisteredDevice}
          disabled={changingDevice}
          className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
            changingDevice ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
          }`}
        >
          {changingDevice ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : (
            'Change Registered Device'
          )}
        </button>
      </div>
    </div>
  </div>
)}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                      {searchTerm ? 'No matching logs found' : 'No unregistered access logs found'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-md">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Previous
            </button>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{Math.min(currentPage * logsPerPage, filteredLogs.length)}</span> of{' '}
                <span className="font-medium">{filteredLogs.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 text-sm font-medium ${
                    currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                
                {/* Page numbers */}
                {[...Array(totalPages).keys()].map((number) => (
                  <button
                    key={number + 1}
                    onClick={() => setCurrentPageNumber(number + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === number + 1
                        ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {number + 1}
                  </button>
                ))}
                
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 text-sm font-medium ${
                    currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
        
        {/* Additional Information */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Understanding Unregistered Access</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-blue-50 p-4 rounded-md">
              <h3 className="text-md font-medium text-blue-800 mb-2">What is unregistered access?</h3>
              <p className="text-sm text-blue-700">
                Unregistered access occurs when a user with an active subscription attempts to access their account from a device
                that is not registered to their subscription. Our system allows only one device per paid subscription.
              </p>
            </div>
            <div className="bg-yellow-50 p-4 rounded-md">
              <h3 className="text-md font-medium text-yellow-800 mb-2">Security considerations</h3>
              <p className="text-sm text-yellow-700">
                Multiple device access attempts could indicate account sharing or a compromised account. Review these logs regularly
                and contact users with suspicious patterns of access.
              </p>
            </div>
            <div className="bg-orange-50 p-4 rounded-md">
              <h3 className="text-md font-medium text-orange-800 mb-2">Repeat offenders</h3>
              <p className="text-sm text-orange-700">
                Users with multiple unregistered access attempts may be deliberately trying to bypass subscription limits. Consider 
                sending warnings or restricting account access for users with 3+ violations.
              </p>
            </div>
          </div>
        </div>
        
      </main>

      {/* Warning Message Modal */}
      {showWarningModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full m-4 max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Send Warning to {userNames[selectedLog?.userId] || (selectedLog?.userEmail ? selectedLog.userEmail.split('@')[0] : 'User')}
              </h3>
              <button
                onClick={closeWarningModal}
                className="text-gray-400 hover:text-gray-500 focus:outline-none"
              >
                <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    This warning will be sent to the user's registered email address and will appear in their account notifications.
                  </p>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <div className="flex items-center mb-2">
                      <div className="text-sm font-medium text-gray-700">To:</div>
                      <div className="ml-2 text-sm text-gray-900">{selectedLog?.userEmail || 'No Email Available'}</div>
                    </div>
                    <div className="flex items-center mb-2">
                      <div className="text-sm font-medium text-gray-700">User ID:</div>
                      <div className="ml-2 text-sm text-gray-900">{selectedLog?.userId || 'Unknown'}</div>
                    </div>
                    <div className="flex items-center mb-2">
                      <div className="text-sm font-medium text-gray-700">Device:</div>
                      <div className="ml-2 text-sm text-gray-900">
                        {selectedLog?.accessDevice?.deviceName || selectedLog?.accessDevice?.model || 'Unknown Device'}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-700">Access Count:</div>
                      <div className="ml-2 text-sm font-semibold text-red-600">{selectedLog ? userAccessCounts[selectedLog.userId] || 1 : 1} times</div>
                    </div>
                  </div>
                </div>
                <div>
                  <label htmlFor="warningMessage" className="block text-sm font-medium text-gray-700 mb-1">
                    Warning Message
                  </label>
                  <textarea
                    id="warningMessage"
                    rows={10}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    value={warningMessage}
                    onChange={(e) => setWarningMessage(e.target.value)}
                  />
                </div>
                <div className="bg-yellow-50 p-3 rounded-md">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-yellow-700">
                        This action will be logged in the system. The user will be notified by email and in-app notification.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-gray-200 flex justify-end space-x-4">
              <button
                onClick={closeWarningModal}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                onClick={sendWarningMessage}
                disabled={sendingWarning || !warningMessage.trim()}
                className={`px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
                  sendingWarning || !warningMessage.trim() ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
              >
                {sendingWarning ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send Warning'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnregisteredAccessPanel;